import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { SquareImportModal } from './SquareImportModal.jsx';
import { SOPEditModal } from './SOPEditModal.jsx';
import { ManageTasksView } from './ManageTasksView.jsx';
import { TimePeriodManager } from './TimePeriodManager.jsx';
import { DisputeHistoryPanel } from './DisputeHistoryPanel.jsx';

// ─── Admin View ───────────────────────────────────────────────────────────────
// `panels` filters the internal tab strip (and the default landing) to a
// subset. Defaults to all panels when omitted. Used to surface different
// slices from different sidebar locations:
//   panels=['users']                                        → Team page
//   panels=['flags','history','suggestions','periods']      → Settings → Tasks
//   panels=undefined                                        → full Settings (legacy)
export function AdminView({ locations, location, tasks, onEditTask, onDeleteTask, onAddTask, onImportTasks, onRefresh, timePeriods, onTimePeriodsChange, panels, currentUser }) {
  const [users, setUsers] = useState([]);
  const [flags, setFlags] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSquare, setShowSquare] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'Barista', location_id: '', pin: '' });
  const [activePanel, setActivePanel] = useState((panels && panels[0]) || 'tasks');
  const [saving, setSaving] = useState(false);
  const [rejectNote, setRejectNote] = useState({});
  const [sopEditTask, setSopEditTask] = useState(null);
  const [assigningUserId, setAssigningUserId] = useState(null);
  const [assignLocIds, setAssignLocIds] = useState([]);

  async function handleSopRemove(task) {
    if (!confirm(`Remove the SOP from "${task.title}"? This will permanently delete all SOP steps.`)) return;
    await api('DELETE', `/api/sops/${task.sop_id}`);
    onRefresh();
  }

  async function loadUsers() { setUsers(await api('GET', '/api/users')); }

  async function handleAssignLocations(userId) {
    await api('PUT', `/api/users/${userId}/locations`, { location_ids: assignLocIds });
    setAssigningUserId(null);
    loadUsers();
  }
  async function loadFlags() { setFlags(await api('GET', `/api/flags/${location.id}`)); }
  async function loadSuggestions() { setSuggestions(await api('GET', `/api/task-suggestions?location_id=${location.id}`)); }

  useEffect(() => { loadUsers(); loadFlags(); loadSuggestions(); }, [location.id]);

  async function handleAddUser(e) {
    e.preventDefault(); setSaving(true);
    // POST /api/users was removed (C3); /api/admin/users generates the PIN and
    // temp password itself, so the form's password/pin fields are not sent.
    const body = { name: userForm.name, email: userForm.email, role: userForm.role,
      location_ids: userForm.location_id ? [Number(userForm.location_id)] : [] };
    try { await api('POST', '/api/admin/users', body); setShowAddUser(false); setUserForm({ name:'',email:'',password:'',role:'Barista',location_id:'',pin:'' }); loadUsers(); }
    finally { setSaving(false); }
  }

  async function handleResolveFlag(id) {
    await api('PUT', `/api/flags/${id}/resolve`, { resolved_by: null });
    loadFlags();
  }

  const openFlags = flags.filter(f => f.status === 'open');
  const pendingSuggestions = suggestions.filter(s => s.status === 'pending');
  const locUsers = users.filter(u => u.location_id === location.id || !u.location_id);

  async function handleApproveSuggestion(id) {
    await api('PUT', `/api/task-suggestions/${id}/approve`, { reviewed_by: null, location_id: location.id });
    loadSuggestions(); onRefresh();
  }
  async function handleRejectSuggestion(id) {
    await api('PUT', `/api/task-suggestions/${id}/reject`, { reviewed_by: null, review_note: rejectNote[id] || null });
    setRejectNote(n => ({ ...n, [id]: '' }));
    loadSuggestions();
  }

  const allAdminTabs = [
    ['tasks',       'Tasks'],
    ['users',       'Team'],
    ['flags',       'Disputes' + (openFlags.length ? ` (${openFlags.length})` : '')],
    ['history',     'Dispute History'],
    ['suggestions', 'Suggestions' + (pendingSuggestions.length ? ` (${pendingSuggestions.length})` : '')],
    ['periods',     'Time Periods'],
  ];
  const adminTabs = panels && panels.length
    ? allAdminTabs.filter(([id]) => panels.includes(id))
    : allAdminTabs;
  // Hide the internal tab strip when the caller already constrains us to
  // a single panel — the sidebar position already carries the meaning.
  const showTabStrip = adminTabs.length > 1;

  return (
    <>
      {showSquare && <SquareImportModal currentUser={currentUser} onClose={() => setShowSquare(false)} onImported={loadUsers} />}
      {sopEditTask && <SOPEditModal sopId={sopEditTask.sop_id} onClose={() => setSopEditTask(null)} onSaved={() => { setSopEditTask(null); onRefresh(); }} />}
      <div>
        {showTabStrip && (
          <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto">
            {adminTabs.map(([id,label]) => (
              <button key={id} onClick={() => setActivePanel(id)}
                className={`px-4 py-2 text-sm font-semibold whitespace-nowrap border-b-2 transition-all ${activePanel === id ? 'border-crimson text-crimson' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                {label}
              </button>
            ))}
          </div>
        )}

        {activePanel === 'tasks' && (
          <>
            {(onAddTask || onImportTasks) && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 12 }}>
                {onAddTask && (
                  <button onClick={onAddTask} className="btn-primary">+ Task</button>
                )}
                {onImportTasks && (
                  <button onClick={onImportTasks} className="btn-secondary">↥ Import</button>
                )}
              </div>
            )}
            <ManageTasksView tasks={tasks} locations={locations} onEdit={onEditTask} onDelete={onDeleteTask}
              onSopEdit={setSopEditTask} onSopRemove={handleSopRemove}
              user={currentUser} onRefresh={onRefresh} />
          </>
        )}

        {activePanel === 'users' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-500">{locUsers.length} team members</p>
              <div className="flex gap-2 flex-wrap justify-end">
                <button onClick={() => setShowSquare(true)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white text-sm rounded-lg font-medium hover:bg-green-700 active:scale-95 transition-all">
                  ⬆ Import from Square
                </button>
                <button onClick={() => setShowAddUser(s => !s)}
                  className="px-3 py-2 bg-crimson text-white text-sm rounded-lg font-medium hover:bg-crimson-dark active:scale-95 transition-all">
                  + Add User
                </button>
              </div>
            </div>

            {showAddUser && (
              <form onSubmit={handleAddUser} className="bg-white rounded-xl border border-gray-200 p-5 mb-4 space-y-3 fade-in shadow-sm">
                <h3 className="font-semibold text-sm text-ink">New Team Member</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input value={userForm.name} onChange={e => setUserForm(f => ({...f, name: e.target.value}))} required
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none"
                    placeholder="Full name *" />
                  <input value={userForm.email} onChange={e => setUserForm(f => ({...f, email: e.target.value}))}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none"
                    placeholder="Email" type="email" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input value={userForm.password} onChange={e => setUserForm(f => ({...f, password: e.target.value}))}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none"
                    placeholder="Password (leave blank to auto-generate)" />
                  <input value={userForm.pin} onChange={e => setUserForm(f => ({...f, pin: e.target.value}))} maxLength={4}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none"
                    placeholder="PIN (kiosk, 4 digits)" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <select value={userForm.role} onChange={e => setUserForm(f => ({...f, role: e.target.value}))}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none">
                    {['Barista','Shift Lead','Manager','Owner'].map(r => <option key={r}>{r}</option>)}
                  </select>
                  <select value={userForm.location_id} onChange={e => setUserForm(f => ({...f, location_id: e.target.value}))}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none">
                    <option value="">All Locations</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setShowAddUser(false)} className="flex-1 py-2 rounded-lg border border-gray-200 text-sm text-gray-600">Cancel</button>
                  <button type="submit" disabled={saving} className="flex-1 py-2 rounded-lg bg-crimson text-white text-sm font-semibold hover:bg-crimson-dark disabled:opacity-50">
                    {saving ? 'Adding...' : 'Add Member'}
                  </button>
                </div>
              </form>
            )}

            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              <div className="divide-y divide-gray-100">
                {locUsers.map(u => {
                  const isMgrOrOwner = ['manager','owner'].includes((u.role||'').toLowerCase());
                  const userLocIds = (() => { try { return JSON.parse(u.location_ids||'[]'); } catch { return []; } })();
                  return (
                    <div key={u.id}>
                      <div className="flex items-center gap-3 px-4 py-3">
                        <div className="w-9 h-9 rounded-full bg-crimson/10 flex items-center justify-center text-crimson font-bold text-sm flex-shrink-0">
                          {u.name[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-ink">{u.name}</p>
                          <p className="text-xs text-gray-400">{u.email || 'No email'}</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {isMgrOrOwner
                              ? <span className="text-xs bg-blue-50 text-blue-500 px-1.5 py-0.5 rounded">All locations</span>
                              : userLocIds.length > 0
                                ? userLocIds.map(lid => { const loc = locations.find(l => l.id === lid); return loc ? <span key={lid} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{loc.name}</span> : null; })
                                : <span className="text-xs text-gray-400 italic">No locations assigned</span>
                            }
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 flex flex-col items-end gap-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.role?.toLowerCase() === 'owner' ? 'bg-gold/20 text-yellow-700' : u.role?.toLowerCase() === 'manager' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                            {u.role}
                          </span>
                          {u.pin && <p className="text-xs text-gray-400">PIN: {u.pin}</p>}
                          {!isMgrOrOwner && (
                            <button onClick={() => {
                              if (assigningUserId === u.id) { setAssigningUserId(null); }
                              else { setAssigningUserId(u.id); setAssignLocIds(userLocIds); }
                            }} className="text-xs text-crimson hover:underline font-medium">
                              {assigningUserId === u.id ? 'Cancel' : 'Assign'}
                            </button>
                          )}
                        </div>
                      </div>
                      {assigningUserId === u.id && (
                        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 space-y-2">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Assign Locations</p>
                          <div className="flex flex-wrap gap-3">
                            {locations.map(loc => (
                              <label key={loc.id} className="flex items-center gap-1.5 text-sm cursor-pointer">
                                <input type="checkbox"
                                  checked={assignLocIds.includes(loc.id)}
                                  onChange={e => setAssignLocIds(ids => e.target.checked ? [...ids, loc.id] : ids.filter(id => id !== loc.id))} />
                                {loc.name}
                              </label>
                            ))}
                          </div>
                          <div className="flex gap-2 pt-1">
                            <button onClick={() => handleAssignLocations(u.id)}
                              className="px-3 py-1.5 bg-crimson text-white text-xs font-semibold rounded-lg hover:bg-crimson-dark active:scale-95 transition-all">
                              Save
                            </button>
                            <button onClick={() => setAssigningUserId(null)}
                              className="px-3 py-1.5 border border-gray-200 text-gray-600 text-xs rounded-lg hover:bg-gray-100 active:scale-95 transition-all">
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activePanel === 'suggestions' && (
          <div>
            <p className="text-sm text-gray-500 mb-4">{pendingSuggestions.length} pending · {suggestions.length} total</p>
            {suggestions.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <p className="text-3xl mb-2">💡</p>
                <p className="text-sm">No suggestions yet</p>
                <p className="text-xs mt-1">Staff can submit suggestions from the Today tab</p>
              </div>
            ) : (
              <div className="space-y-3">
                {suggestions.map(s => (
                  <div key={s.id} className={`rounded-xl border p-4 ${s.status==='pending' ? 'border-amber-200 bg-amber-50' : s.status==='approved' ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'}`}>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <p className="font-semibold text-sm text-ink">{s.title}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${s.status==='pending' ? 'bg-amber-100 text-amber-700' : s.status==='approved' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {s.status}
                          </span>
                          {s.suggested_period && <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full border border-blue-100">{s.suggested_period}</span>}
                        </div>
                        {s.description && <p className="text-sm text-gray-600 mb-1">{s.description}</p>}
                        <p className="text-xs text-gray-400">By {s.submitted_by_name} · {new Date(s.created_at).toLocaleDateString()}</p>
                        {s.review_note && <p className="text-xs text-gray-500 mt-1 italic">Note: {s.review_note}</p>}
                      </div>
                    </div>
                    {s.status === 'pending' && (
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <button onClick={() => handleApproveSuggestion(s.id)}
                          className="px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 active:scale-95 transition-all">
                          ✓ Approve & Create Task
                        </button>
                        <input value={rejectNote[s.id]||''} onChange={e => setRejectNote(n=>({...n,[s.id]:e.target.value}))}
                          className="flex-1 min-w-24 border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-ink bg-white focus:outline-none"
                          placeholder="Rejection note (optional)" />
                        <button onClick={() => handleRejectSuggestion(s.id)}
                          className="px-3 py-1.5 bg-red-50 text-red-600 text-xs font-semibold rounded-lg hover:bg-red-100 active:scale-95 transition-all">
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activePanel === 'periods' && (
          <div>
            <TimePeriodManager timePeriods={timePeriods} onChange={onTimePeriodsChange} />
            <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-200 text-center">
              <p className="text-xs text-gray-400 font-medium">🗓 Coming soon: shift-based task lists</p>
              <p className="text-xs text-gray-400 mt-0.5">Tasks will automatically filter by shift and sync completions across all on-shift staff.</p>
            </div>
          </div>
        )}

        {activePanel === 'history' && (
          <DisputeHistoryPanel locationId={location.id} />
        )}

        {activePanel === 'flags' && (
          <div>
            <p className="text-sm text-gray-500 mb-4">{openFlags.length} open disputes</p>
            {flags.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <p className="text-3xl mb-2">🏁</p>
                <p className="text-sm">No disputes reported</p>
              </div>
            ) : (
              <div className="space-y-3">
                {flags.map(flag => (
                  <div key={flag.id} className={`rounded-xl border p-4 ${flag.status === 'open' ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-sm font-bold text-ink">{flag.task_title}</span>
                          {flag.status === 'open'
                            ? <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold">🚩 Open</span>
                            : <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">✓ Resolved</span>
                          }
                        </div>
                        <p className="text-sm text-gray-700 mb-2">{flag.description}</p>
                        <p className="text-xs text-gray-400">
                          Reported by {flag.flagged_by_name} · {new Date(flag.flagged_at).toLocaleDateString()}
                          {flag.completed_by_name && <span className="text-amber-600 font-medium"> · Completed by {flag.completed_by_name}</span>}
                          {flag.resolved_by_name && ` · Resolved by ${flag.resolved_by_name}`}
                        </p>
                        {flag.photo_url && (
                          <img src={flag.photo_url} alt="Evidence" className="mt-2 w-20 h-16 object-cover rounded-lg border border-gray-200 cursor-pointer" onClick={() => window.open(flag.photo_url)} />
                        )}
                      </div>
                      {flag.status === 'open' && (
                        <button onClick={() => handleResolveFlag(flag.id)}
                          className="px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 flex-shrink-0">
                          Resolve
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
